import { createPortal } from 'react-dom';
import { useStrategyStore } from '@/stores/strategy';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  X, Target, ExternalLink, Loader2, ChevronRight,
  MessageSquare, BarChart3, Globe, Users, TrendingUp,
  Lightbulb, CheckCircle2, Clock,
} from 'lucide-react';
import type { StrategyHistoryYear } from '@/types';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';
import { cn } from '@/lib/utils';
import { StrategyHistoryComparisonChart } from '@/components/strategy/charts/StrategyHistoryComparisonChart';
import { StrategyChannelCharts } from '@/components/strategy/charts/StrategyChannelCharts';
import { StrategyMarketProfileRadar } from '@/components/strategy/charts/StrategyMarketProfileRadar';
import { StrategyKpiComparisonBars } from '@/components/strategy/charts/StrategyKpiComparisonBars';
import { hasAnyMarketProfileSignal } from '@/components/strategy/charts/strategy-chart-shared';

const YEAR_RANGE = 5;
const CURRENT_YEAR = new Date().getFullYear();

interface StrategyPanelProps {
  onNavigate?: (path: string) => void;
}

export function StrategyPanel({ onNavigate }: StrategyPanelProps) {
  const { isPanelOpen, strategy, isLoading, activeYear, closePanel, openPanel } = useStrategyStore();
  const [historyData, setHistoryData] = useState<StrategyHistoryYear[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!isPanelOpen) return;
    if (strategy?.id) {
      setHistoryLoading(true);
      api.get<ApiResponse<StrategyHistoryYear[]>>(`/marketing-strategies/${strategy.id}/history-context`)
        .then((res) => setHistoryData(Array.isArray(res.data) ? res.data : []))
        .catch(() => setHistoryData([]))
        .finally(() => setHistoryLoading(false));
    }
  }, [isPanelOpen, strategy?.id]);

  if (!isPanelOpen) return null;

  const yearOptions = Array.from({ length: YEAR_RANGE }, (_, i) => CURRENT_YEAR - 1 + i);

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-foreground/40 transition-opacity"
        onClick={closePanel}
        aria-hidden
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[700px] flex-col border-l border-border bg-card shadow-2xl"
        role="dialog"
        aria-label="Marketingstrategie"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-card-hover">
              <Target size={18} className="text-muted" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Marketingstrategie
              </h2>
              <p className="text-xs text-muted">
                {strategy ? strategy.title : 'Keine Strategie geladen'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Year selector */}
            <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
              {yearOptions.map((y) => (
                <button
                  key={y}
                  onClick={() => openPanel(y)}
                  className={cn(
                    'rounded px-2 py-1 text-xs font-medium transition-colors',
                    activeYear === y
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted hover:bg-card-hover'
                  )}
                >
                  {y}
                </button>
              ))}
            </div>
            {onNavigate && strategy && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onNavigate('/marketing-strategy'); closePanel(); }}
                className="gap-1.5"
              >
                <ExternalLink size={13} />
                Öffnen
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={closePanel}>
              <X size={18} />
            </Button>
          </div>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 size={32} className="animate-spin text-muted" />
          </div>
        ) : !strategy ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="rounded-full bg-card-hover p-4">
              <Target size={28} className="text-muted" />
            </div>
            <div>
              <p className="font-medium text-muted dark:text-foreground">
                Keine Strategie für {activeYear}
              </p>
              <p className="mt-1 text-sm text-muted">
                Erstelle eine Marketingstrategie für dieses Jahr.
              </p>
            </div>
            {onNavigate && (
              <Button
                onClick={() => { onNavigate('/marketing-strategy'); closePanel(); }}
                className="gap-2"
              >
                <Target size={14} />
                Strategie erstellen
              </Button>
            )}
          </div>
        ) : (
          <Tabs defaultValue="overview" className="flex flex-1 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-border px-6">
              <TabsList className="h-10 rounded-none border-none bg-transparent p-0 gap-0">
                {[
                  { value: 'overview', label: 'Übersicht' },
                  { value: 'goals', label: 'Ziele & KPIs' },
                  { value: 'channels', label: 'Kanäle & Botschaften' },
                  { value: 'history', label: 'Vorjahre' },
                ].map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="h-10 rounded-none border-b-2 border-transparent px-4 text-sm font-medium data-[state=active]:border-border data-[state=active]:text-foreground data-[state=active]:shadow-none dark:data-[state=active]:border-border dark:data-[state=active]:text-primary-foreground"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              {/* ── Übersicht ── */}
              <TabsContent value="overview" className="m-0 p-6 space-y-5">
                {strategy.status !== 'active' && (
                  <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
                    <Clock size={14} />
                    Status: <span className="font-medium capitalize">{strategy.status === 'draft' ? 'Entwurf' : 'Archiviert'}</span>
                  </div>
                )}

                {strategy.executive_summary ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-muted">Executive Summary</h3>
                    <p className="text-sm leading-relaxed text-muted">
                      {strategy.executive_summary}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted italic">Kein Executive Summary vorhanden. Generiere die Strategie mit KI.</p>
                )}

                {strategy.target_market && (
                  <div className="space-y-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-muted">
                      <Users size={14} />
                      Zielmarkt
                    </h3>
                    {strategy.target_market.segments?.length > 0 && (
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-muted">Segmente</p>
                        <div className="flex flex-wrap gap-1.5">
                          {strategy.target_market.segments.map((s) => (
                            <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {strategy.target_market.geography?.length > 0 && (
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-muted">Geografie</p>
                        <div className="flex flex-wrap gap-1.5">
                          {strategy.target_market.geography.map((g) => (
                            <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {strategy.competitive_landscape && (strategy.competitive_landscape.competitors?.length > 0) && (
                  <div className="space-y-2">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-muted">
                      <Globe size={14} />
                      Wettbewerb
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {strategy.competitive_landscape.competitors.map((c) => (
                        <Badge key={c} variant="outline" className="text-xs border-border bg-background text-muted dark:bg-card-hover">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {strategy.lessons_learned && (
                  <div className="space-y-2 rounded-lg border border-border bg-background p-4 dark:bg-card/40">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-muted">
                      <Lightbulb size={14} />
                      Lessons Learned aus Vorjahren
                    </h3>
                    <p className="text-sm leading-relaxed text-muted">
                      {strategy.lessons_learned}
                    </p>
                  </div>
                )}

                {hasAnyMarketProfileSignal(strategy) && (
                  <div className="rounded-lg border border-border bg-background/80 p-3 dark:bg-card-hover/40">
                    <StrategyMarketProfileRadar strategy={strategy} compact />
                  </div>
                )}
              </TabsContent>

              {/* ── Ziele & KPIs ── */}
              <TabsContent value="goals" className="m-0 p-6 space-y-5">
                {strategy.objectives && strategy.objectives.length > 0 ? (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted">Strategische Ziele</h3>
                    {strategy.objectives.map((obj) => (
                      <div key={obj.id} className="rounded-lg border border-border p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-muted" />
                            <div>
                              <p className="text-sm font-medium text-foreground">{obj.title}</p>
                              <p className="mt-0.5 text-xs text-muted">KPI: {obj.kpi} · Ziel: {obj.target}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-xs">{obj.timeframe}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted italic">Keine Ziele definiert.</p>
                )}

                {strategy.kpis && strategy.kpis.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-muted">
                      <BarChart3 size={14} />
                      KPIs
                    </h3>
                    <div className="grid gap-3">
                      {strategy.kpis.map((kpi) => {
                        const pct = kpi.current != null && kpi.target > 0
                          ? Math.min(100, Math.round((kpi.current / kpi.target) * 100))
                          : null;
                        return (
                          <div key={kpi.id} className="rounded-lg border border-border p-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium text-muted">{kpi.name}</span>
                              <span className="text-muted">
                                {kpi.current != null ? `${kpi.current} / ` : ''}{kpi.target} {kpi.unit}
                              </span>
                            </div>
                            {pct !== null && (
                              <div className="mt-2">
                                <Progress value={pct} className="h-1.5" />
                                <p className="mt-1 text-right text-xs text-muted">{pct}%</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <StrategyKpiComparisonBars kpis={strategy.kpis} compact />
                  </div>
                )}
              </TabsContent>

              {/* ── Kanäle & Botschaften ── */}
              <TabsContent value="channels" className="m-0 p-6 space-y-5">
                {strategy.channel_strategy?.channels && strategy.channel_strategy.channels.length > 0 ? (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-muted">Kanalstrategie</h3>
                    {[...strategy.channel_strategy.channels]
                      .sort((a, b) => b.priority - a.priority)
                      .map((ch) => (
                        <div key={ch.name} className="rounded-lg border border-border p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">{ch.name}</span>
                              <Badge variant="secondary" className="text-xs">
                                Prio {ch.priority}/10
                              </Badge>
                            </div>
                            <span className="text-sm font-medium text-muted">
                              {ch.budget_pct}% Budget
                            </span>
                          </div>
                          {ch.tactics && ch.tactics.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {ch.tactics.map((t) => (
                                <span key={t} className="rounded bg-card-hover px-2 py-0.5 text-xs text-muted">
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    <div className="rounded-lg border border-border bg-background/80 p-2 pt-4 dark:bg-card-hover/40">
                      <StrategyChannelCharts channels={strategy.channel_strategy.channels} compact />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted italic">Keine Kanalstrategie definiert.</p>
                )}

                {strategy.key_messages && strategy.key_messages.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-muted">
                      <MessageSquare size={14} />
                      Kernbotschaften
                    </h3>
                    {strategy.key_messages.map((msg) => (
                      <div key={msg.id} className="rounded-lg border border-border p-3">
                        <p className="text-sm text-muted">"{msg.message}"</p>
                        <div className="mt-1.5 flex gap-2">
                          <Badge variant="outline" className="text-xs">{msg.audience}</Badge>
                          <Badge variant="secondary" className="text-xs">{msg.channel}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── Vorjahre ── */}
              <TabsContent value="history" className="m-0 p-6">
                {historyLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 size={24} className="animate-spin text-muted" />
                  </div>
                ) : historyData.length === 0 ? (
                  <p className="text-center text-sm text-muted italic py-8">
                    Keine Vorjahresdaten verfügbar.
                  </p>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-border bg-background/80 p-3 dark:bg-card-hover/40">
                      <p className="mb-2 text-xs font-medium text-muted">Vergleich Vorjahre</p>
                      <StrategyHistoryComparisonChart years={historyData} compact />
                    </div>
                    {historyData.map((year) => (
                      <div key={year.year} className="rounded-lg border border-border p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground">
                            {year.year} – {year.title}
                          </h3>
                          <Badge
                            variant={year.completion_rate >= 75 ? 'default' : year.completion_rate >= 50 ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {year.completion_rate}% Abschluss
                          </Badge>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-muted">Aktivitäten</p>
                            <p className="font-medium text-muted">
                              {year.activities_done}/{year.activities_total} erledigt
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted">Budget-Auslastung</p>
                            <p className="font-medium text-muted">
                              {year.budget_utilization}%
                            </p>
                          </div>
                        </div>

                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-muted mb-1">
                            <span>Abschlussrate</span>
                            <span>{year.completion_rate}%</span>
                          </div>
                          <Progress value={year.completion_rate} className="h-1.5" />
                        </div>

                        {year.top_activities.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-muted mb-1.5">Top-Aktivitäten</p>
                            <div className="space-y-1">
                              {year.top_activities.slice(0, 3).map((act) => (
                                <div key={act.id} className="flex items-center gap-1.5 text-xs text-muted">
                                  <TrendingUp size={10} className="text-muted shrink-0" />
                                  <span className="truncate">{act.title}</span>
                                  <Badge variant="outline" className="shrink-0 text-[10px] px-1 py-0">{act.type}</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="text-center text-xs text-muted pt-2">
                      Diese Daten fliessen in die KI-Strategiegenerierung ein.
                    </p>
                  </div>
                )}
              </TabsContent>
            </ScrollArea>

            {/* Footer with link to full page */}
            {onNavigate && (
              <div className="shrink-0 border-t border-border px-6 py-3">
                <button
                  onClick={() => { onNavigate('/marketing-strategy'); closePanel(); }}
                  className="flex w-full items-center justify-center gap-2 text-sm font-medium text-muted hover:text-foreground dark:hover:text-muted"
                >
                  <span>Zur vollständigen Strategieseite</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </Tabs>
        )}
      </div>
    </>
  );

  return createPortal(content, document.body);
}
